import { ticketingFetch } from "./client";

export type CommercialClassification =
  | "UNCLASSIFIED"
  | "FOC"
  | "WARRANTY"
  | "CHARGEABLE";

/**
 * Maps frontend domain classification values to backend wire-format values.
 *
 * The backend API accepts: "FOC", "Warranty", "Chargeable", "Unclassified".
 * Frontend domain constants ("FOC", "WARRANTY", "CHARGEABLE", "UNCLASSIFIED")
 * are kept stable and translated only at the API boundary to avoid implicit
 * case-transformation bugs.
 */
export const CLASSIFICATION_WIRE_VALUES: Record<
  Exclude<CommercialClassification, "UNCLASSIFIED">,
  string
> = {
  FOC: "FOC",
  WARRANTY: "Warranty",
  CHARGEABLE: "Chargeable",
} as const;

/** Backend wire → frontend domain classification map. */
const CLASSIFICATION_DOMAIN_MAP: Record<string, Exclude<CommercialClassification, "UNCLASSIFIED">> = {
  FOC: "FOC",
  Warranty: "WARRANTY",
  Chargeable: "CHARGEABLE",
};

function classificationWireValue(domain: CommercialClassification): string {
  if (domain === "UNCLASSIFIED") return "Unclassified";
  return CLASSIFICATION_WIRE_VALUES[domain];
}

/**
 * Normalizes a backend wire classification value into the frontend domain
 * representation. Preserves null (unclassified). Unknown strings are not
 * coerced — they are dropped to null so callers never receive an unrecognized
 * value that bypasses the domain contract.
 */
function classificationDomainValue(
  wire: string | null | undefined,
): Exclude<CommercialClassification, "UNCLASSIFIED"> | null {
  if (wire == null) return null;
  return CLASSIFICATION_DOMAIN_MAP[wire] ?? null;
}

/**
 * Normalizes the `classification` field on a ticket object returned from the
 * backend so that every ticket leaving the API layer uses the frontend domain
 * representation.
 */
function normalizeTicket<T extends { classification?: unknown }>(ticket: T): T {
  return {
    ...ticket,
    classification: classificationDomainValue(ticket.classification as string | null | undefined),
  };
}

export type ChargeableRequestState =
  | "pending"
  | "proceed"
  | "decline"
  | "clarified";

export type ChargeableCustomerState =
  | "awaiting_your_decision"
  | "clarification_requested"
  | "decision_recorded";

export type TicketCreatePayload = {
  title: string;
  description: string;
  request_type_id: number;
  /**
   * Optional deprecated field. The Stage 10C3C1 backend derives the routing
   * department from the selected Request Category and ignores this field.
   * Frontend ticket-creation call sites (Requester and Staff) should omit
   * this value entirely so the backend's category-derived routing is used.
   */
  owner_department_id?: number | null;
  priority: "Low" | "Medium" | "High" | "Critical";
  target_date: string;
};

export interface ClassifyTicketPayload {
  classification: CommercialClassification;
  assessment_note: string;
  reason?: string;
}

export interface ChargeableRequestPayload {
  chargeable_explanation: string;
}

export interface ChargeableResponsePayload {
  generation: number;
  response: "proceed" | "decline" | "clarify";
  clarification_text?: string;
}

export type TicketListItem = {
  id: number;
  ticket_no: string;
  title: string;
  status: string;
  priority: string;
  request_type_id: number;
  request_type_name: string | null;
  owner_department_id: number | null;
  owner_department_name: string | null;
  requester_id: number;
  requester_name: string | null;
  assignee_id: number | null;
  assignee_name: string | null;
  target_date: string;
  overdue: boolean;
  aging_days: number;
  created_at: string | null;
  classification: Exclude<CommercialClassification, "UNCLASSIFIED"> | null;
  closure_reason: string | null;
};

export type TicketDetail = TicketListItem & {
  description: string;
  first_response_at: string | null;
  resolved_at: string | null;
  closed_at: string | null;
  reopen_count: number;
  last_activity_at: string | null;
  updated_at: string | null;
  assessment_note: string | null;
  chargeable_request_generation: number | null;
  chargeable_request_state: ChargeableRequestState | null;
  chargeable_explanation: string | null;
  chargeable_requested_at: string | null;
  chargeable_responded_at: string | null;
  chargeable_clarification_text: string | null;
  chargeable_decision_required: boolean;
  chargeable_customer_state: ChargeableCustomerState | null;
};

export type TicketComment = {
  id: number;
  ticket_id: number;
  author_id: number;
  author_name: string | null;
  author_role: string | null;
  comment_text: string;
  comment_type: string;
  is_internal: boolean;
  created_at: string | null;
  updated_at: string | null;
};

export type TicketActivity = {
  id: number;
  ticket_id: number;
  actor_id: number | null;
  actor_name: string | null;
  actor_role: string | null;
  event_type: string;
  old_value: string | null;
  new_value: string | null;
  remarks: string | null;
  created_at: string | null;
};

export type TicketFilters = {
  search?: string;
  status?: string;
  priority?: string;
  request_type_id?: string;
  owner_department_id?: string;
  overdue_only?: string;
  classification?: "FOC" | "WARRANTY" | "CHARGEABLE";
};

export type WorkflowActionResponse = {
  message: string;
  ticket: TicketDetail;
};

export type AssignTicketPayload = {
  owner_department_id?: number | null;
  assignee_id?: number | null;
};

export type ReassignTicketPayload = AssignTicketPayload & {
  reason: string;
};

export class TicketApiError extends Error {
  status: number;
  errorCode: string | null;
  detail: string;

  constructor(status: number, detail: string, errorCode: string | null) {
    super(detail);
    this.name = "TicketApiError";
    this.status = status;
    this.errorCode = errorCode;
    this.detail = detail;
  }
}

async function request<T>(path: string, options: { body?: string; method?: string } = {}): Promise<T> {
  const response = await ticketingFetch(path, {
    ...options,
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    let errorDetail = `Request failed with ${response.status}`;
    let errorCode: string | null = null;
    try {
      const errorBody = (await response.json()) as { detail?: unknown; error_code?: unknown };
      if (typeof errorBody.detail === "string" && errorBody.detail.length > 0) {
        errorDetail = errorBody.detail;
      }
      if (typeof errorBody.error_code === "string" && errorBody.error_code.length > 0) {
        errorCode = errorBody.error_code;
      }
    } catch {
      // Non-JSON error body; fall back to the status-only message.
    }
    throw new TicketApiError(response.status, errorDetail, errorCode);
  }

  return (await response.json()) as T;
}

export function createTicket(payload: TicketCreatePayload): Promise<TicketDetail> {
  return request<TicketDetail>("/tickets", {
    method: "POST",
    body: JSON.stringify(payload),
  }).then(normalizeTicket);
}

export function listTickets(filters: TicketFilters = {}): Promise<TicketListItem[]> {
  const searchParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (!value) return;
    if (key === "classification") {
      searchParams.set("classification", classificationWireValue(value as CommercialClassification));
    } else {
      searchParams.set(key, value);
    }
  });
  const query = searchParams.toString();
  return request<TicketListItem[]>(`/tickets${query ? `?${query}` : ""}`).then((items) =>
    items.map(normalizeTicket),
  );
}

export function listStaffAssignedTickets(): Promise<TicketListItem[]> {
  return request<TicketListItem[]>("/tickets/staff/assigned").then((items) => items.map(normalizeTicket));
}

export function listStaffAvailableTickets(): Promise<TicketListItem[]> {
  return request<TicketListItem[]>("/tickets/staff/available").then((items) => items.map(normalizeTicket));
}

export function listStaffSubmittedTickets(): Promise<TicketListItem[]> {
  return request<TicketListItem[]>("/tickets/staff/submitted").then((items) => items.map(normalizeTicket));
}

export function getTicket(ticketId: number): Promise<TicketDetail> {
  return request<TicketDetail>(`/tickets/${ticketId}`).then(normalizeTicket);
}

export function listTicketComments(ticketId: number): Promise<TicketComment[]> {
  return request<TicketComment[]>(`/tickets/${ticketId}/comments`);
}

export function addTicketComment(
  ticketId: number,
  commentText: string,
  isInternal: boolean = false,
): Promise<TicketComment> {
  return request<TicketComment>(`/tickets/${ticketId}/comments`, {
    method: "POST",
    body: JSON.stringify({ comment_text: commentText, is_internal: isInternal }),
  });
}

export function listTicketActivity(ticketId: number): Promise<TicketActivity[]> {
  return request<TicketActivity[]>(`/tickets/${ticketId}/activity`);
}

function workflowRequest(
  ticketId: number,
  action: string,
  payload?: Record<string, unknown>,
): Promise<WorkflowActionResponse> {
  return request<WorkflowActionResponse>(`/tickets/${ticketId}/${action}`, {
    method: "POST",
    body: JSON.stringify(payload ?? {}),
  }).then((response) => ({
    ...response,
    ticket: normalizeTicket(response.ticket),
  }));
}

export function assignTicket(ticketId: number, payload: AssignTicketPayload): Promise<WorkflowActionResponse> {
  return workflowRequest(ticketId, "assign", payload);
}

export function takeTicket(ticketId: number): Promise<WorkflowActionResponse> {
  return workflowRequest(ticketId, "take");
}

export function reassignTicket(ticketId: number, payload: ReassignTicketPayload): Promise<WorkflowActionResponse> {
  return workflowRequest(ticketId, "reassign", payload);
}

export function unassignTicket(ticketId: number): Promise<WorkflowActionResponse> {
  return workflowRequest(ticketId, "unassign");
}

export function startTicket(ticketId: number): Promise<WorkflowActionResponse> {
  return workflowRequest(ticketId, "start");
}

export function setWaitingRequester(ticketId: number, reason: string): Promise<WorkflowActionResponse> {
  return workflowRequest(ticketId, "waiting-requester", { reason });
}

export function setWaitingInternal(ticketId: number, reason: string): Promise<WorkflowActionResponse> {
  return workflowRequest(ticketId, "waiting-internal", { reason });
}

export function returnInProgress(ticketId: number): Promise<WorkflowActionResponse> {
  return workflowRequest(ticketId, "return-in-progress");
}

export function resolveTicket(ticketId: number, resolutionComment: string): Promise<WorkflowActionResponse> {
  return workflowRequest(ticketId, "resolve", { resolution_comment: resolutionComment });
}

export function closeTicket(ticketId: number): Promise<WorkflowActionResponse> {
  return workflowRequest(ticketId, "close");
}

export function reopenTicket(ticketId: number, reason: string): Promise<WorkflowActionResponse> {
  return workflowRequest(ticketId, "reopen", { reason });
}

export function classifyTicket(
  ticketId: number,
  payload: ClassifyTicketPayload,
): Promise<WorkflowActionResponse> {
  const wirePayload: Record<string, unknown> = {
    ...payload,
    classification: classificationWireValue(payload.classification),
  };
  return workflowRequest(ticketId, "classify", wirePayload);
}

export function issueChargeableRequest(
  ticketId: number,
  payload: ChargeableRequestPayload,
): Promise<WorkflowActionResponse> {
  return workflowRequest(ticketId, "chargeable-request", payload as unknown as Record<string, unknown>);
}

export function respondToChargeableRequest(
  ticketId: number,
  payload: ChargeableResponsePayload,
): Promise<WorkflowActionResponse> {
  return workflowRequest(ticketId, "chargeable-response", payload as unknown as Record<string, unknown>);
}
